import React, { FC, useEffect, useState, useMemo, useCallback, useReducer } from 'react';
import { API, Endpoints, Project, ProjectAPI } from '@brg/api';
import { useToasts } from '@brg/ui';
import { useProfile } from 'core/auth';
import { ProfileRole, bProfile } from '@brg/auth';
import { useTranslation } from 'react-i18next';
import { HsePolicies } from 'shared/constants/HsePolicies';
import { CheckboxRowWrapper, OkButton, OkButtonWrapper, Outer, TooltipList } from './toolsStyles';
import { MagicBoxRow } from 'shared/components/MagicBoxRow';
import { AppTester } from 'supporting';

const BASIC_DESIGN_TAG_ID = '978saa51s67df76432e70310be98';

let cachedContexts: any[] | null = null;
let contextsRequest: Promise<any[]> | null = null;

const fetchContexts = (): Promise<any[]> => {
  if (cachedContexts) {
    return Promise.resolve(cachedContexts);
  }
  if (!contextsRequest) {
    contextsRequest = API.request()
      .endpoint(Endpoints.geograffee)
      .url('/api/Team/GetContexts')
      .get()
      .send()
      .then((data) => data.json())
      .then((data) => {
        cachedContexts = Array.isArray(data) ? data : [];
        return cachedContexts;
      })
      .finally(() => {
        contextsRequest = null;
      });
  }
  return contextsRequest;
};

const reducer = (state: any, action: any): any => {
  switch (action.type) {
    case 'CONTEXT_TOGGLED': {
      const { checked, initialValue } = state[action.payload];
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          checked: !checked,
          changed: initialValue !== !checked,
        }
      };
    }
    case 'VALUES_REINITIALIZED': {
      const changedContexts = action.payload.reduce((result: any, context: any) => {
        const { checked } = state[context];
        return {
          ...result,
          [context]: {
            ...state[context],
            initialValue: checked,
            changed: false,
          }
        }
      }, {})
      return {
        ...state,
        ...changedContexts,
      };
    }
    case 'CHECKBOXES_LOADED':
      return action.payload;
    default:
      return state;
  }
}

const toggleContext = (payload: any) => ({
  type: 'CONTEXT_TOGGLED',
  payload,
})

const reinitializeValues = (payload: string[]) => ({
  type: 'VALUES_REINITIALIZED',
  payload,
})

const loadCheckboxes = (payload: any) => ({
  type: 'CHECKBOXES_LOADED',
  payload,
})

type PropsT = {
  project?: any;
  onShouldReload?: (project: Project) => void;
  onClose?: () => void;
  fullWidth?: boolean;
};

export const ProjectShowcasePanel: FC<PropsT> = ({
  project,
  onShouldReload,
  onClose,
  fullWidth,
}) => {
  const profile = useProfile();
  const toasts = useToasts();
  const { i18n: { language } } = useTranslation();
  const isGeo = AppTester.isGeograffee();
  const isContinuumSite = AppTester.isContinuumSite();
  const [contextCheckboxes, setContextCheckboxes] = useState<any[]>(cachedContexts ?? []);

  const isModer = useMemo(() => profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)), [profile]);

  const canSuperviseProject = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_supervise_project)).containsIn(profile.rolesList)
  ), [profile]);

  const canClearCuratorsProject = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_clear_curators_lst_project)).containsIn(profile.rolesList)
  ), [profile]);

  const hasContextsSuperviserPolicy = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.contexts_superviser)).containsIn(profile.rolesList)
  ), [profile]);

  const canSuperviseContextNames = useMemo(
    () => (profile?.rolesList
          ?.filter(x => x.startsWith(HsePolicies.can_supervise_context_prefix))
          ?.map(x => x.replace(HsePolicies.can_supervise_context_prefix, '')) ?? []
  ), [profile]);

  useEffect(() => {
    if (!profile) return;
    let isMounted = true;
    fetchContexts().then((data) => {
      if (isMounted) {
        setContextCheckboxes(data);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [profile]);

  const [state, dispatch] = useReducer<any>(reducer, {});

  const initialCheckboxes = useMemo(() => {
    if (!contextCheckboxes?.length || !profile) {
      return null;
    }

    return API.lang(contextCheckboxes, language).reduce((result, { teamName, moderRole, contextName }: any) => {
      const visible = profile?.rolesList?.includes(moderRole)
        || canSuperviseContextNames.includes(contextName)
        || hasContextsSuperviserPolicy;

      if (!visible) {
        return result;
      }

      const hasContext = project?.contexts?.includes(contextName);

      return {
        ...result,
        [contextName]: {
          initialValue: hasContext,
          label: teamName,
          checked: hasContext,
          changed: false,
          visible: true,
        }
      };
    }, {});
  }, [
    contextCheckboxes,
    language,
    project?.contexts,
    profile,
    canSuperviseContextNames,
    hasContextsSuperviserPolicy,
  ]);

  useEffect(() => {
    if (initialCheckboxes && Object.keys(initialCheckboxes).length) {
      dispatch(loadCheckboxes(initialCheckboxes));
    }
  }, [initialCheckboxes]);

  const [tp, stp] = useState<Project<'Global'>>(project);
  const [isSelfCuratorCurrent, setIsSelfCuratorCurrent] = useState(false);
  const [removeCuratorsCurrent, setRemoveCuratorsCurrent] = useState(false);

  const isSelfCurator = !!(profile && tp?.curatorsIdList.includes(profile.accountId));

  const isTeacher = useMemo(() => profile && bProfile.isEmployee(profile), [profile]);

  const isOwnProject = !!project && (project.author?.accountId === (profile?.accountId) || project?.coAuthors?.some((v: any) => v?.accountId === (profile?.accountId)));

  const isStudentProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'student'), [project]);
  const isAlumniProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'hsealumni'), [project]);
  const isSelfCuratorChanged = useMemo(() => isSelfCurator !== isSelfCuratorCurrent, [isSelfCurator, isSelfCuratorCurrent]);
  const changedContexts = useMemo(() => {
    return Object.entries(state).reduce((result, [context, { changed }]: [any, any]) => {
      if (changed) {
        return [...result, context]
      }
      return result;
    }, [] as string[]);
  }, [state])

  const isExceptionTag = useMemo(() => (project?.tagsID?.includes(BASIC_DESIGN_TAG_ID)) , [project]);

  const isButtonDisabled = !changedContexts.length && !isSelfCuratorChanged && !removeCuratorsCurrent;

  const canBeCurated = useMemo(() => {
    const { approvals } = project;
    const rejectedAtHsedesign = approvals?.some(({ contextService, isApproved }: any) => {
      return ['hsedesign', 'hsedesigntemp'].includes(contextService) && !isApproved;
    })

    return isExceptionTag || project.contexts?.includes('hsedesign') || rejectedAtHsedesign;
  }, [project, isExceptionTag]);

  const handleSelfCuratorChange = useCallback(() => {
    if (!isSelfCurator) {
      if (canBeCurated) {
        setIsSelfCuratorCurrent(!isSelfCuratorCurrent);
      } else {
        toasts.error("Для взятия кураторство необходимо, чтобы проект прошел модерацию супервайзерами");
      }
    } else {
      toasts.error(`Для снятия кураторства необходимо обратиться к Ларцеву А.`);
    }
  }, [isSelfCuratorCurrent, isSelfCurator, canBeCurated, toasts]);

  const handleRemoveCuratorsChange = useCallback(() => {
    setRemoveCuratorsCurrent(!removeCuratorsCurrent);
  }, [removeCuratorsCurrent]);

  const reloadStp = useCallback(() => {
    if (project) {
      ProjectAPI.Get({ idProject: project.projectId })
        .then((v) => v.json())
        .then(stp);
    }
  }, [project]);

  const setSelfCurator = useCallback(async () => {
    if (tp?.curatorsIdList?.length) {
      toasts.error('Куратор уже закреплен за проектом');
      return;
    }

    if (profile && tp && !isSelfCurator) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/SetCurrentUserCuratorProject`)
        .post()
        .body({
          projectId: project.projectId,
        })
        .send()

      onShouldReload?.({ ...project, curatorsIdList: [...project.curatorsIdList, profile.accountId] });

      reloadStp();

      toasts.info(`Поздравляем! Теперь Вы куратор проекта ${project.title}`);
    } else {
      toasts.error('Вы уже являетесь куратором данного проекта');
    }
  }, [isSelfCurator, onShouldReload, profile, project, toasts, tp, reloadStp]);

  const removeCurators = useCallback(async () => {
    if (profile && tp) {
      await API.fetch(`${Endpoints.geograffee}/api/Project/ClearCurators?projectId=${project?.projectId}`, {});

      onShouldReload?.({ ...project, curatorsIdList: [...project.curatorsIdList, profile.accountId] });

      reloadStp();

      toasts.info(`Список кураторов очищен`);
    } else {
      toasts.error('Не удалось очистить список кураторов');
    }
  }, [onShouldReload, profile, project, toasts, tp, reloadStp]);

  const handleOk = useCallback(() => {
    const contexts: any[] = [];

    Object.entries(state).forEach(([context, {
      changed,
      checked,
    }]: [any, any]) => {
      if (changed) {
        contexts.push({
          context,
          operation: checked ? 1 : 0,
        })
        toasts.info(checked ? `Проект добавлен на витрину ${context}` : `Проект убран с витрины ${context}`);
      }
    })

    // if (isSelfCuratorChanged) {
    //   void setSelfCurator();
    // }

    // if (removeCuratorsCurrent) {
    //   void removeCurators()
    // }

    if (contexts.length) {
      API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/UpdateContexts`)
        .post()
        .body({
          projectId: project.projectId,
          contexts,
        })
        .send()
        .then(() => {
          dispatch(reinitializeValues(changedContexts))
        })
    }
    onClose?.();
  }, [
    isSelfCuratorChanged,
    setSelfCurator,
    removeCuratorsCurrent,
    removeCurators,
    state,
    toasts,
    onClose,
    project.projectId,
    changedContexts,
  ]);

  useEffect(() => {
    setIsSelfCuratorCurrent(isSelfCurator);
  }, [isSelfCurator]);

  const checkboxes = Object.entries(state).map(([context, {
    checked,
    label,
  }]: [any, any]) => (
    <CheckboxRowWrapper key={context}>
      <MagicBoxRow
        onClick={() => dispatch(toggleContext(context))}
        checked={checked}
        label={label}
      />
    </CheckboxRowWrapper>
  ))

  const hasCuratorOptions = (isTeacher || isModer || canSuperviseProject) && (isStudentProject || isAlumniProject) && !isOwnProject && !tp?.curatorsIdList?.length;
  const hasRemoveCuratorsOption = ['f5a705e1e8cb4a80b58582423d51dd0e', '01d7846ac1a34b6f8182646495d1fda6'].includes(profile?.accountId || '') || canClearCuratorsProject;

  if (!checkboxes.length && !hasCuratorOptions && !hasRemoveCuratorsOption) {
    return null;
  }

  return (
    <Outer isGeo={isGeo} isContinuumSite={isContinuumSite} fullWidth={fullWidth}>
      <TooltipList>
        {checkboxes}
        {/* {hasCuratorOptions && (
          <CheckboxRowWrapper>
            <MagicBoxRow
              onClick={handleSelfCuratorChange}
              checked={isSelfCuratorCurrent}
              label="взять кураторство"
            />
          </CheckboxRowWrapper>
        )}
        {hasRemoveCuratorsOption && (
          <CheckboxRowWrapper>
            <MagicBoxRow
              onClick={handleRemoveCuratorsChange}
              checked={removeCuratorsCurrent}
              label="снять кураторство"
            />
          </CheckboxRowWrapper>
        )} */}
      </TooltipList>
      <OkButtonWrapper>
        <OkButton disabled={isButtonDisabled} isGeo={isGeo} isContinuumSite={isContinuumSite} onClick={handleOk}>Подтвердить</OkButton>
      </OkButtonWrapper>
    </Outer>
  );
};
import React, { FC, useEffect, useState, useMemo, useCallback, useReducer } from 'react';
import { API, Endpoints, Project, ProjectAPI } from '@brg/api';
import { useToasts } from '@brg/ui';
import { useProfile } from 'core/auth';
import { ProfileRole, bProfile } from '@brg/auth';
import { useTranslation } from 'react-i18next';
import { HsePolicies } from 'shared/constants/HsePolicies';
import { CheckboxRowWrapper, OkButton, OkButtonWrapper, Outer, TooltipList } from './toolsStyles';
import { MagicBoxRow } from 'shared/components/MagicBoxRow';
import { AppTester } from 'supporting';

const BASIC_DESIGN_TAG_ID = '978saa51s67df76432e70310be98';

let cachedContexts: any[] | null = null;
let contextsRequest: Promise<any[]> | null = null;

const fetchContexts = (): Promise<any[]> => {
  if (cachedContexts) {
    return Promise.resolve(cachedContexts);
  }
  if (!contextsRequest) {
    contextsRequest = API.request()
      .endpoint(Endpoints.geograffee)
      .url('/api/Team/GetContexts')
      .get()
      .send()
      .then((data) => data.json())
      .then((data) => {
        cachedContexts = Array.isArray(data) ? data : [];
        return cachedContexts;
      })
      .finally(() => {
        contextsRequest = null;
      });
  }
  return contextsRequest;
};

const reducer = (state: any, action: any): any => {
  switch (action.type) {
    case 'CONTEXT_TOGGLED': {
      const { checked, initialValue } = state[action.payload];
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          checked: !checked,
          changed: initialValue !== !checked,
        }
      };
    }
    case 'VALUES_REINITIALIZED': {
      const changedContexts = action.payload.reduce((result: any, context: any) => {
        const { checked } = state[context];
        return {
          ...result,
          [context]: {
            ...state[context],
            initialValue: checked,
            changed: false,
          }
        }
      }, {})
      return {
        ...state,
        ...changedContexts,
      };
    }
    case 'CHECKBOXES_LOADED':
      return action.payload;
    default:
      return state;
  }
}

const toggleContext = (payload: any) => ({
  type: 'CONTEXT_TOGGLED',
  payload,
})

const reinitializeValues = (payload: string[]) => ({
  type: 'VALUES_REINITIALIZED',
  payload,
})

const loadCheckboxes = (payload: any) => ({
  type: 'CHECKBOXES_LOADED',
  payload,
})

type PropsT = {
  project?: any;
  onShouldReload?: (project: Project) => void;
  onClose?: () => void;
  fullWidth?: boolean;
};

export const ProjectShowcasePanel: FC<PropsT> = ({
  project,
  onShouldReload,
  onClose,
  fullWidth,
}) => {
  const profile = useProfile();
  const toasts = useToasts();
  const { i18n: { language } } = useTranslation();
  const isGeo = AppTester.isGeograffee();
  const isContinuumSite = AppTester.isContinuumSite();
  const [contextCheckboxes, setContextCheckboxes] = useState<any[]>(cachedContexts ?? []);

  const isModer = useMemo(() => profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)), [profile]);

  const canSuperviseProject = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_supervise_project)).containsIn(profile.rolesList)
  ), [profile]);

  const canClearCuratorsProject = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_clear_curators_lst_project)).containsIn(profile.rolesList)
  ), [profile]);

  const hasContextsSuperviserPolicy = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.contexts_superviser)).containsIn(profile.rolesList)
  ), [profile]);

  const canSuperviseContextNames = useMemo(
    () => (profile?.rolesList
          ?.filter(x => x.startsWith(HsePolicies.can_supervise_context_prefix))
          ?.map(x => x.replace(HsePolicies.can_supervise_context_prefix, '')) ?? []
  ), [profile]);

  useEffect(() => {
    if (!profile) return;
    let isMounted = true;
    fetchContexts().then((data) => {
      if (isMounted) {
        setContextCheckboxes(data);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [profile]);

  const [state, dispatch] = useReducer<any>(reducer, {});

  const initialCheckboxes = useMemo(() => {
    if (!contextCheckboxes?.length || !profile) {
      return null;
    }

    return API.lang(contextCheckboxes, language).reduce((result, { teamName, moderRole, contextName }: any) => {
      const visible = profile?.rolesList?.includes(moderRole)
        || canSuperviseContextNames.includes(contextName)
        || hasContextsSuperviserPolicy;

      if (!visible) {
        return result;
      }

      const hasContext = project?.contexts?.includes(contextName);

      return {
        ...result,
        [contextName]: {
          initialValue: hasContext,
          label: teamName,
          checked: hasContext,
          changed: false,
          visible: true,
        }
      };
    }, {});
  }, [
    contextCheckboxes,
    language,
    project?.contexts,
    profile,
    canSuperviseContextNames,
    hasContextsSuperviserPolicy,
  ]);

  useEffect(() => {
    if (initialCheckboxes && Object.keys(initialCheckboxes).length) {
      dispatch(loadCheckboxes(initialCheckboxes));
    }
  }, [initialCheckboxes]);

  const [tp, stp] = useState<Project<'Global'>>(project);
  const [isSelfCuratorCurrent, setIsSelfCuratorCurrent] = useState(false);
  const [removeCuratorsCurrent, setRemoveCuratorsCurrent] = useState(false);

  const isSelfCurator = !!(profile && tp?.curatorsIdList.includes(profile.accountId));

  const isTeacher = useMemo(() => profile && bProfile.isEmployee(profile), [profile]);

  const isOwnProject = !!project && (project.author?.accountId === (profile?.accountId) || project?.coAuthors?.some((v: any) => v?.accountId === (profile?.accountId)));

  const isStudentProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'student'), [project]);
  const isAlumniProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'hsealumni'), [project]);
  const isSelfCuratorChanged = useMemo(() => isSelfCurator !== isSelfCuratorCurrent, [isSelfCurator, isSelfCuratorCurrent]);
  const changedContexts = useMemo(() => {
    return Object.entries(state).reduce((result, [context, { changed }]: [any, any]) => {
      if (changed) {
        return [...result, context]
      }
      return result;
    }, [] as string[]);
  }, [state])

  const isExceptionTag = useMemo(() => (project?.tagsID?.includes(BASIC_DESIGN_TAG_ID)) , [project]);

  const isButtonDisabled = !changedContexts.length && !isSelfCuratorChanged && !removeCuratorsCurrent;

  const canBeCurated = useMemo(() => {
    const { approvals } = project;
    const rejectedAtHsedesign = approvals?.some(({ contextService, isApproved }: any) => {
      return ['hsedesign', 'hsedesigntemp'].includes(contextService) && !isApproved;
    })

    return isExceptionTag || project.contexts?.includes('hsedesign') || rejectedAtHsedesign;
  }, [project, isExceptionTag]);

  const handleSelfCuratorChange = useCallback(() => {
    if (!isSelfCurator) {
      if (canBeCurated) {
        setIsSelfCuratorCurrent(!isSelfCuratorCurrent);
      } else {
        toasts.error("Для взятия кураторство необходимо, чтобы проект прошел модерацию супервайзерами");
      }
    } else {
      toasts.error(`Для снятия кураторства необходимо обратиться к Ларцеву А.`);
    }
  }, [isSelfCuratorCurrent, isSelfCurator, canBeCurated, toasts]);

  const handleRemoveCuratorsChange = useCallback(() => {
    setRemoveCuratorsCurrent(!removeCuratorsCurrent);
  }, [removeCuratorsCurrent]);

  const reloadStp = useCallback(() => {
    if (project) {
      ProjectAPI.Get({ idProject: project.projectId })
        .then((v) => v.json())
        .then(stp);
    }
  }, [project]);

  const setSelfCurator = useCallback(async () => {
    if (tp?.curatorsIdList?.length) {
      toasts.error('Куратор уже закреплен за проектом');
      return;
    }

    if (profile && tp && !isSelfCurator) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/SetCurrentUserCuratorProject`)
        .post()
        .body({
          projectId: project.projectId,
        })
        .send()

      onShouldReload?.({ ...project, curatorsIdList: [...project.curatorsIdList, profile.accountId] });

      reloadStp();

      toasts.info(`Поздравляем! Теперь Вы куратор проекта ${project.title}`);
    } else {
      toasts.error('Вы уже являетесь куратором данного проекта');
    }
  }, [isSelfCurator, onShouldReload, profile, project, toasts, tp, reloadStp]);

  const removeCurators = useCallback(async () => {
    if (profile && tp) {
      await API.fetch(`${Endpoints.geograffee}/api/Project/ClearCurators?projectId=${project?.projectId}`, {});

      onShouldReload?.({ ...project, curatorsIdList: [...project.curatorsIdList, profile.accountId] });

      reloadStp();

      toasts.info(`Список кураторов очищен`);
    } else {
      toasts.error('Не удалось очистить список кураторов');
    }
  }, [onShouldReload, profile, project, toasts, tp, reloadStp]);

  const handleOk = useCallback(() => {
    const contexts: any[] = [];

    Object.entries(state).forEach(([context, {
      changed,
      checked,
    }]: [any, any]) => {
      if (changed) {
        contexts.push({
          context,
          operation: checked ? 1 : 0,
        })
        toasts.info(checked ? `Проект добавлен на витрину ${context}` : `Проект убран с витрины ${context}`);
      }
    })

    // if (isSelfCuratorChanged) {
    //   void setSelfCurator();
    // }

    // if (removeCuratorsCurrent) {
    //   void removeCurators()
    // }

    if (contexts.length) {
      API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/UpdateContexts`)
        .post()
        .body({
          projectId: project.projectId,
          contexts,
        })
        .send()
        .then(() => {
          dispatch(reinitializeValues(changedContexts))
        })
    }
    onClose?.();
  }, [
    isSelfCuratorChanged,
    setSelfCurator,
    removeCuratorsCurrent,
    removeCurators,
    state,
    toasts,
    onClose,
    project.projectId,
    changedContexts,
  ]);

  useEffect(() => {
    setIsSelfCuratorCurrent(isSelfCurator);
  }, [isSelfCurator]);

  const checkboxes = Object.entries(state).map(([context, {
    checked,
    label,
  }]: [any, any]) => (
    <CheckboxRowWrapper key={context}>
      <MagicBoxRow
        onClick={() => dispatch(toggleContext(context))}
        checked={checked}
        label={label}
      />
    </CheckboxRowWrapper>
  ))

  const hasCuratorOptions = (isTeacher || isModer || canSuperviseProject) && (isStudentProject || isAlumniProject) && !isOwnProject && !tp?.curatorsIdList?.length;
  const hasRemoveCuratorsOption = ['f5a705e1e8cb4a80b58582423d51dd0e', '01d7846ac1a34b6f8182646495d1fda6'].includes(profile?.accountId || '') || canClearCuratorsProject;

  if (!checkboxes.length && !hasCuratorOptions && !hasRemoveCuratorsOption) {
    return null;
  }

  return (
    <Outer isGeo={isGeo} isContinuumSite={isContinuumSite} fullWidth={fullWidth}>
      <TooltipList>
        {checkboxes}
        {/* {hasCuratorOptions && (
          <CheckboxRowWrapper>
            <MagicBoxRow
              onClick={handleSelfCuratorChange}
              checked={isSelfCuratorCurrent}
              label="взять кураторство"
            />
          </CheckboxRowWrapper>
        )}
        {hasRemoveCuratorsOption && (
          <CheckboxRowWrapper>
            <MagicBoxRow
              onClick={handleRemoveCuratorsChange}
              checked={removeCuratorsCurrent}
              label="снять кураторство"
            />
          </CheckboxRowWrapper>
        )} */}
      </TooltipList>
      <OkButtonWrapper>
        <OkButton disabled={isButtonDisabled} isGeo={isGeo} isContinuumSite={isContinuumSite} onClick={handleOk}>Подтвердить</OkButton>
      </OkButtonWrapper>
    </Outer>
  );
};
